/* Two modes, chosen by the `data-keep-open` attribute on the host.
   With it, the disclosure is an accordion: it only closes when its own
   summary is pressed. Without it, it is a dropdown — it also closes on a
   press outside, on Escape (focus returns to the summary) and when focus
   leaves it, and arrow keys rove through the panel's controls. */
class DetailsDisclosure extends HTMLElement {
  #cleanups = [];
  #pressedInside = false;

  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));

    // Enter bypass — some on-page script (haven't traced which exact
    // app/handler, but we saw the symptom in PDP accordion: Enter on a
    // <summary> would fire two clicks via dispatchEvent and net to no
    // toggle). Capture-phase keydown lets us flip `open` ourselves
    // before any of those handlers run. Click is left to the native
    // browser handler; the previous header-drawer click failure was
    // caused by `data-mb-dropdown` on the <details>, which routed
    // clicks through modern-bold.js's initDropdowns() and
    // preventDefault'd them — that attribute is gone now.
    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary) {
      summary.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          event.stopImmediatePropagation();
          if (this.#isHoverMenu()) this.#reopen();
          else this.mainDetailsToggle.toggleAttribute('open');
        },
        true
      );
      summary.addEventListener('click', (event) => {
        if (!this.#isHoverMenu()) return;
        event.preventDefault();
        this.#reopen();
      });
      summary.addEventListener('mouseenter', () => {
        if (this.#isHoverMenu()) this.#reopen();
      });
    }
  }

  connectedCallback() {
    const onKeydown = (event) => this.#onEscape(event);
    document.addEventListener('keydown', onKeydown);
    this.#cleanups.push(() => document.removeEventListener('keydown', onKeydown));

    // Everything below is the dropdown (auto-close) mode only.
    if (this.hasAttribute('data-keep-open')) return;

    // A press outside dismisses. A press inside is remembered because Safari
    // doesn't focus what it clicks: the focusout it fires would otherwise
    // close the panel before the click reached the link.
    const onPointerDown = (event) => {
      this.#pressedInside = this.contains(event.target);
      if (!this.#pressedInside && this.mainDetailsToggle.open) this.close();
    };
    document.addEventListener('pointerdown', onPointerDown, { capture: true });
    this.#cleanups.push(() => document.removeEventListener('pointerdown', onPointerDown, { capture: true }));

    const onPanelKeydown = (event) => this.#onPanelKeydown(event);
    this.addEventListener('keydown', onPanelKeydown);
    this.#cleanups.push(() => this.removeEventListener('keydown', onPanelKeydown));
  }

  disconnectedCallback() {
    this.#cleanups.forEach((cleanup) => cleanup());
    this.#cleanups = [];
  }

  // Desktop header dropdowns stay [open] and show on hover or focus, so a
  // summary toggle would hide the links until reload. There, Escape dismisses
  // the panel until the pointer comes back to the summary or focus leaves.
  #isHoverMenu() {
    return (
      this.mainDetailsToggle.matches('.mb-header__nav .mb-header__nav-item--has-children') &&
      window.matchMedia('(min-width: 990px)').matches
    );
  }

  #reopen() {
    this.mainDetailsToggle.setAttribute('open', '');
  }

  #onEscape(event) {
    if (event.key !== 'Escape' || !this.mainDetailsToggle.open) return;
    const summary = this.mainDetailsToggle.querySelector('summary');
    const active = document.activeElement;
    if (this.#isHoverMenu()) {
      if (!this.mainDetailsToggle.contains(active) && !this.mainDetailsToggle.matches(':hover')) return;
      if (this.content.contains(active)) summary.focus();
      this.mainDetailsToggle.removeAttribute('open');
      return;
    }
    if (this.hasAttribute('data-keep-open')) return;
    // Hand focus back to the trigger unless it is busy elsewhere on the page.
    if (this.contains(active) || active === document.body) summary.focus();
    this.close();
  }

  // Arrow keys step through the panel's controls; from the summary ArrowDown
  // enters the first and ArrowUp the last.
  #onPanelKeydown(event) {
    // A key press ends the pointer press, so a stale flag can't hold us open.
    this.#pressedInside = false;
    if (!this.mainDetailsToggle.open) return;
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    if (event.target.matches('input, textarea, select')) return;
    const items = [...this.content.querySelectorAll('a[href], button:not([disabled])')];
    if (!items.length) return;
    event.preventDefault();
    const last = items.length - 1;
    const current = items.indexOf(document.activeElement);
    let next;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else if (current === -1) next = event.key === 'ArrowDown' ? 0 : last;
    else next = (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[next].focus();
  }

  onFocusOut(event) {
    if (this.#isHoverMenu() && !this.mainDetailsToggle.contains(event.relatedTarget)) this.#reopen();
    // Header dropdowns want auto-close-on-blur, but accordion items do
    // NOT — opening a panel and tabbing into the body content shouldn't
    // collapse the panel. Hosts opt out by adding `data-keep-open`.
    if (this.hasAttribute('data-keep-open')) return;
    setTimeout(() => {
      const pressed = this.#pressedInside;
      this.#pressedInside = false;
      if (!pressed && !this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    // close() only ever writes false, so open has to be published here.
    const open = this.mainDetailsToggle.open;
    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary?.hasAttribute('aria-expanded')) summary.setAttribute('aria-expanded', String(open));
    if (!open) this.#pressedInside = false;

    // Hover menus animate with transitions; replaying cached ones would flash the panel.
    if (this.#isHoverMenu()) return;
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
